/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useState } from "react";
import type { FaxDetail, FaxReviewInput, ReviewPerson } from "../api/faxes";
import { Button } from "../components/ui/Button";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { Select } from "../components/ui/Select";
import { Textarea } from "../components/ui/Textarea";
import {
  organizationChoices,
  providerChoices,
  providersForOrganization,
  type CandidateChoice,
} from "../lib/faxCandidates";

const AS_INTAKE = "intake";
const CREATE_NEW = "new";

type Choice = string;

type Option = { value: Choice; label: string };

function options(
  choices: CandidateChoice[],
  matchedId: number | null,
  matchedOffered: boolean,
  noun: string,
): Option[] {
  const listed: Option[] = [];
  if (matchedOffered && matchedId !== null) {
    listed.push({ value: AS_INTAKE, label: `Use the matched ${noun} (${matchedId})` });
  }
  for (const choice of choices) {
    if (choice.id !== matchedId) {
      listed.push({ value: String(choice.id), label: `Use existing: ${choice.label} (${choice.id})` });
    }
  }
  listed.push({ value: CREATE_NEW, label: `Create a new ${noun} from the fields below` });
  return listed;
}

function person(source: ReviewPerson | null | undefined, defaults: Partial<ReviewPerson>): ReviewPerson {
  return {
    firstName: source?.firstName ?? defaults.firstName ?? null,
    lastName: source?.lastName ?? defaults.lastName ?? null,
    dateOfBirth: source?.dateOfBirth ?? defaults.dateOfBirth ?? null,
    phone: source?.phone ?? defaults.phone ?? null,
    email: source?.email ?? defaults.email ?? null,
    stateCode: source?.stateCode ?? defaults.stateCode ?? null,
  };
}

function blankToNull(value: string): string | null {
  return value.trim() === "" ? null : value.trim();
}

/**
 * The review step before a referral is created from a fax: the extracted family pre-filled and
 * editable, and for each registry side either the intake's match, a candidate, or a new record
 * from the reviewed fields. The verbatim extraction stays on the case; this is what gets created.
 */
export function FaxReviewModal({
  detail,
  busy,
  onClose,
  onConfirm,
}: {
  detail: FaxDetail;
  busy: boolean;
  onClose: () => void;
  onConfirm: (review: FaxReviewInput) => void;
}) {
  const extraction = detail.extraction;
  const { summary } = detail;
  const [patient, setPatient] = useState<ReviewPerson>(
    person(null, {
      firstName: extraction?.patient?.firstName ?? null,
      lastName: extraction?.patient?.lastName ?? null,
      dateOfBirth: extraction?.patient?.dateOfBirth ?? null,
      stateCode: extraction?.patient?.stateCode ?? null,
    }),
  );
  const [guardian, setGuardian] = useState<ReviewPerson>(
    person(null, {
      firstName: extraction?.guardian?.firstName ?? null,
      lastName: extraction?.guardian?.lastName ?? null,
      phone: extraction?.guardian?.phone ?? null,
      email: extraction?.guardian?.email ?? null,
    }),
  );
  const organizationOptions = options(
    organizationChoices(detail.organizationCandidates),
    detail.organizationId,
    summary.organizationVerdict === "MATCH",
    "organization",
  );
  const [organizationChoice, setOrganizationChoice] = useState<Choice>(
    organizationOptions[0]?.value ?? CREATE_NEW,
  );
  const [providerChoice, setProviderChoice] = useState<Choice>(AS_INTAKE);
  const selectedOrganizationId =
    organizationChoice === AS_INTAKE
      ? detail.organizationId
      : organizationChoice === CREATE_NEW
        ? null
        : Number(organizationChoice);
  const providerOptions = options(
    providersForOrganization(providerChoices(detail.providerCandidates), selectedOrganizationId),
    detail.providerId,
    summary.providerVerdict === "MATCH" && detail.providerOrganizationId === selectedOrganizationId,
    "provider",
  );
  const providerOffered = providerOptions.some((option) => option.value === providerChoice);
  const effectiveProviderChoice = providerOffered
    ? providerChoice
    : (providerOptions[0]?.value ?? CREATE_NEW);
  const [organizationName, setOrganizationName] = useState(extraction?.referringOrganization?.name ?? "");
  const [organizationState, setOrganizationState] = useState(
    extraction?.referringOrganization?.stateCode ?? "",
  );
  const [organizationDomain, setOrganizationDomain] = useState(
    extraction?.referringOrganization?.emailDomain ?? "",
  );
  const [providerFirst, setProviderFirst] = useState(extraction?.referringProvider?.firstName ?? "");
  const [providerLast, setProviderLast] = useState(extraction?.referringProvider?.lastName ?? "");
  const [providerEmail, setProviderEmail] = useState(extraction?.referringProvider?.email ?? "");
  const [providerPhone, setProviderPhone] = useState(extraction?.referringProvider?.phoneNumber ?? "");
  const [providerFax, setProviderFax] = useState(extraction?.referringProvider?.faxNumber ?? "");
  const [note, setNote] = useState("");

  const guardianNamed = (guardian.firstName ?? "").trim() !== "" || (guardian.lastName ?? "").trim() !== "";

  function build(): FaxReviewInput {
    const organization =
      organizationChoice === AS_INTAKE
        ? null
        : organizationChoice === CREATE_NEW
          ? {
              existingId: null,
              create: {
                name: organizationName.trim(),
                stateCode: blankToNull(organizationState),
                emailDomain: blankToNull(organizationDomain),
              },
            }
          : { existingId: Number(organizationChoice), create: null };
    const provider =
      effectiveProviderChoice === AS_INTAKE
        ? null
        : effectiveProviderChoice === CREATE_NEW
          ? {
              existingId: null,
              create: {
                firstName: blankToNull(providerFirst),
                lastName: blankToNull(providerLast),
                email: blankToNull(providerEmail),
                phoneNumber: blankToNull(providerPhone),
                faxNumber: blankToNull(providerFax),
              },
            }
          : { existingId: Number(effectiveProviderChoice), create: null };
    return { patient, guardian, organization, provider, note: blankToNull(note) };
  }

  const field = (
    label: string,
    value: string | null,
    onChange: (next: string) => void,
    type: string = "text",
  ) => (
    <Input
      label={label}
      type={type}
      value={value ?? ""}
      onChange={(event) => onChange(event.target.value)}
    />
  );

  return (
    <Modal isOpen title="Review and create the referral" size="lg" onClose={onClose}>
      <div className="space-y-6 px-6 py-5">
        <p className="text-sm leading-relaxed text-text-secondary">
          Check what the fax said before it becomes a referral. Edits here are what gets created;
          the fax and its extraction stay on the case unchanged.
        </p>

        <section className="space-y-3">
          <h3 className="font-display text-base text-text-primary">Patient</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {field("First name", patient.firstName, (v) => setPatient({ ...patient, firstName: v }))}
            {field("Last name", patient.lastName, (v) => setPatient({ ...patient, lastName: v }))}
            {field("Date of birth (YYYY-MM-DD)", patient.dateOfBirth, (v) =>
              setPatient({ ...patient, dateOfBirth: v }),
            )}
            {field("State", patient.stateCode, (v) => setPatient({ ...patient, stateCode: v }))}
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="font-display text-base text-text-primary">Caregiver</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {field("First name", guardian.firstName, (v) => setGuardian({ ...guardian, firstName: v }))}
            {field("Last name", guardian.lastName, (v) => setGuardian({ ...guardian, lastName: v }))}
            {field("Phone", guardian.phone, (v) => setGuardian({ ...guardian, phone: v }), "tel")}
            {field("Email", guardian.email, (v) => setGuardian({ ...guardian, email: v }), "email")}
          </div>
          {!guardianNamed && (
            <ErrorMessage message="A caregiver name is required to create a referral." />
          )}
        </section>

        <section className="space-y-3">
          <h3 className="font-display text-base text-text-primary">Referring organization</h3>
          <Select
            label="Organization"
            value={organizationChoice}
            onChange={setOrganizationChoice}
            options={organizationOptions}
          />
          {organizationChoice === CREATE_NEW && (
            <div className="grid gap-3 sm:grid-cols-2">
              {field("Name", organizationName, setOrganizationName)}
              {field("State", organizationState, setOrganizationState)}
              {field("Email domain", organizationDomain, setOrganizationDomain)}
            </div>
          )}
        </section>

        <section className="space-y-3">
          <h3 className="font-display text-base text-text-primary">Referring provider</h3>
          <Select
            label="Provider"
            value={effectiveProviderChoice}
            onChange={setProviderChoice}
            options={providerOptions}
          />
          {selectedOrganizationId === null && (
            <p className="text-sm text-text-secondary">
              A new organization has no providers on file yet, so the provider is created with it.
            </p>
          )}
          {effectiveProviderChoice === CREATE_NEW && (
            <div className="grid gap-3 sm:grid-cols-2">
              {field("First name", providerFirst, setProviderFirst)}
              {field("Last name", providerLast, setProviderLast)}
              {field("Email", providerEmail, setProviderEmail, "email")}
              {field("Phone", providerPhone, setProviderPhone, "tel")}
              {field("Fax", providerFax, setProviderFax, "tel")}
            </div>
          )}
        </section>

        <Textarea
          label="Note for the referral (optional)"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Anything the family or the clinic told us"
        />

        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" disabled={busy || !guardianNamed} onClick={() => onConfirm(build())}>
            {busy ? "Creating…" : "Create referral"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
